import { FC } from "react";
import { useTranslation } from "react-i18next";
import CommentIconSVG from "../../../resources/svg/CommentIconSVG";
import useCustomFormik from "../../../../custom-hooks/useCustomFormik";
import {
    applicationCreationFields,
    applicationCreationSchemaFactory,
} from "../../../../validation-schemas/work-schemas/ApplicationSchema";
import CurrencyIconSVG from "../../../resources/svg/CurrencyIconSVG";
import CustomTextArea from "../../../../custom-components/form-inputs/CustomTextArea";
import CustomError from "../../../../custom-components/form-inputs/CustomError";
import { useAppDispatch } from "../../../../custom-hooks/useAppDispatch";
import { ApplicationServiceImpl } from "../../../../services/ApplicationServiceImpl";
import { WorkDTO } from "../../../../domain/models/dto/WorkDTO";
import PriceInput from "../../../resources/components/form-inputs/PriceInput";
import { ApplicationCreationForm } from "../../../../domain/models/dto/ApplicationDTO";
import { StatusCodes } from "http-status-codes";
import { useErrorHandler } from "../../../../custom-hooks/base-hooks/useErrorHandler";

interface CreateApplicationProps {
    work: WorkDTO;
    successCallback: (messageKey: string) => void;
}

const CreateApplication: FC<CreateApplicationProps> = (props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { resetErrors: resetForbiddenErrors, errorKey: forbiddenErrorKey } = useErrorHandler(
        StatusCodes.FORBIDDEN,
        "work.applicationCreation.forbiddenError"
    );

    const { form, setErrorKey, errorKey } = useCustomFormik({
        initial: null,
        fields: applicationCreationFields,
        schema: applicationCreationSchemaFactory(t),
        onSubmit: async (values) => {
            resetForbiddenErrors();
            const work = await dispatch(
                ApplicationServiceImpl.createApplicationThunk({ ...values, workId: props.work.id })
            );
            if (work.meta.requestStatus !== "fulfilled") {
                setErrorKey("errors.common");
                return;
            }
            props.successCallback("work.applicationCreation.creationSuccess");
        },
    });

    return (
        <div className="flex flex-col items-center w-full py-10 bg-backgroundBlock border border-backgroundButton rounded-lg mb-[60px] dmax2:mb-10">
            <h2 className="H24B dmax2:H20B text-textWhite mb-8 dmax2:mb-7">{t("work.applicationCreation.title")}</h2>
            <div className="relative dmin2:w-[842px] di6:w-[690px] td5:w-[550px] tmax4:w-[328px]">
                <div className="relative flex flex-col justify-start w-full">
                    <div className="flex items-center mb-4">
                        <CommentIconSVG className="mr-3 dmax2:mr-2 text-textWhite" />
                        <p className="H20B dmax2:H16B text-textWhite">{t("work.applicationCreation.comment")}</p>
                    </div>
                    <CustomTextArea
                        form={form}
                        name="comment"
                        fields={applicationCreationFields}
                        charactersRestrictionKey="common.characters"
                        restrainerClassName="!h-[150px]"
                    />
                    <CustomError form={form} name="comment" className="mt-2 dmax2:mt-1" />
                </div>
                <div className="relative flex flex-col">
                    <div className="flex items-center mt-5">
                        <CurrencyIconSVG className="w-8 dmax2:w-6 h-8 dmax2:h-6 mr-3 dmax2:mr-2 text-textWhite" />
                        <p className="H20B dmax2:H16B text-textWhite mr-3">{t("workCreation.fields.price")}</p>
                    </div>
                    <div className="flex items-center mt-5 dmax2:mt-4">
                        <PriceInput form={form} name="price" className="w-[191px] dmax2:w-[180px]" />
                        <span className="H20B dmax2:H16B text-textFaded ml-5 dmax2:ml-3">
                            {t("work.applicationCreation.priceUnits")}
                        </span>
                    </div>
                    <CustomError
                        form={form}
                        name={"price.amount" as keyof ApplicationCreationForm}
                        className="mt-2 dmax2:mt-1"
                    />
                </div>
                <CustomError errorKey={forbiddenErrorKey || errorKey} className="mt-2 dmax2:mt-1" />
                <div
                    onClick={() => form.handleSubmit()}
                    className="button H16B dmax2:H14B w-[193px] dmax2:w-[175px] h-[50px] dmax2:h-10 mt-9 dmax2:mt-7"
                >
                    {t("work.applicationCreation.action")}
                </div>
            </div>
        </div>
    );
};

export default CreateApplication;
